import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ProCalculatorMain from './ProCalculatorMain';
import ProCalculatorSignInPrompt from './ProCalculatorSignInPrompt';
import ProjectManager from './ProjectManager';
import Auth from './Auth';
import supabase from '../lib/supabase';
import { checkSubscription } from '../utils/subscriptionUtils';
import '../styles/ProCalculatorWrapper.css';
import '../styles/Preview.css';

const ProCalculatorWrapper = ({ 
  isDarkMode, 
  isPreview = false, 
  user = null, 
  savedState, // Accept savedState prop
  activeSection = 'reference',
  setActiveSection = () => {},
  onStateChange, // Accept the prop
  isMetric = false // Accept the isMetric prop
}) => {
  // If user is passed as prop, use it; otherwise use the auth context
  const authContext = useAuth();
  const contextUser = authContext?.user;
  const signOut = authContext?.signOut;
  
  // Use the prop user if provided, otherwise use the context user
  const currentUser = user || contextUser;
  const [showProjectManager, setShowProjectManager] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [notification, setNotification] = useState({ message: '', visible: false });
  const notificationTimeoutRef = useRef(null);
  
  // Handle escape key press to close modals
  const handleEscapeKey = useCallback((event) => {
    if (event.key === 'Escape') {
      setShowSubscriptionModal(false);
      setShowProjectManager(false);
    }
  }, []);

  // Add and remove event listener for escape key
  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [handleEscapeKey]);
  
  // Check for authentication on mount and when user changes
  useEffect(() => {
    let isMounted = true; // Track mounted state
    const checkAuthAndProfile = async () => {
      if (currentUser) {
        // User is authenticated
        
        try {
          // Fetch user profile
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', currentUser.id)
            .single();
            
          if (!isMounted) return; // Exit if unmounted

          if (error) {
            // Error handling for profile fetch
            setUserProfile(null);
            setAuthError('Error loading user profile. Please try again.');
          } else {
            // Profile successfully loaded
            setUserProfile(profile);
            setAuthError(null);
          }
        } catch (err) {
          if (!isMounted) return; // Exit if unmounted
          // Error handling for unexpected profile fetch issues
          setUserProfile(null);
          setAuthError('Error loading user profile. Please try again.');
        }
      } else {
        if (!isMounted) return; // Exit if unmounted
        setUserProfile(null);
      }
    };
    
    checkAuthAndProfile();

    return () => {
      isMounted = false; // Cleanup function
    };
  }, [currentUser]);
  
  // Check if user has an active subscription
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Check subscription status when user changes
  useEffect(() => {
    let isMounted = true; // Track mounted state
    const checkUserSubscription = async () => {
      if (currentUser) {
        // Check subscription status - only users with valid subscriptions get access
        const subscriptionStatus = await checkSubscription(currentUser.id);
        
        if (!isMounted) return; // Exit if unmounted

        setIsSubscribed(subscriptionStatus.isSubscribed);
        setSubscriptionDetails(subscriptionStatus);
        setIsAdmin(subscriptionStatus.isAdmin || false);
        // Subscription status checked
      } else {
        if (!isMounted) return; // Exit if unmounted
        setIsSubscribed(false);
        setSubscriptionDetails(null);
        setIsAdmin(false);
      }
    };
    
    checkUserSubscription();

    return () => {
      isMounted = false; // Cleanup function
    };
  }, [currentUser]);
  const [initialAuthTab, setInitialAuthTab] = useState('login');
  
  // Function to display notifications
  const showNotification = useCallback((message) => {
    setNotification({ message, visible: true });
    
    // Auto-hide project manager
    setShowProjectManager(false);
    
    // Clear any existing timeout
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    
    // Auto-hide notification after 7 seconds
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(prev => ({ ...prev, visible: false }));
    }, 7000);
  }, []);

  // Handle loading a project into the calculator
  const handleLoadProject = (projectData) => {
    // Pass the loaded project data to the ProCalculator component
    if (projectData && typeof projectData === 'object') {
      if (typeof projectData === 'string') {
        try {
          projectData = JSON.parse(projectData);
        } catch (e) {
          console.error('Error parsing project data:', e);
          return;
        }
      }
      // setCalculatorState(projectData); // Removed internal state
    }
  };
  
  // Handle project actions (save/load/delete)
  const handleProjectAction = (action, message) => {
    // Show notification
    setNotification({ message, visible: true });
    
    // Auto-hide project manager
    setShowProjectManager(false);
    
    // Clear any existing timeout
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    
    // Auto-hide notification after 7 seconds
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(prev => ({ ...prev, visible: false }));
    }, 7000);
  };
  
  // Toggle project manager visibility
  const toggleProjectManager = () => {
    if (!user) {
      setShowSubscriptionModal(true);
      return;
    }
    
    // All logged-in users can access project manager for now
    setShowProjectManager(!showProjectManager);
  };
  
  // Render subscription modal
  const renderSubscriptionModal = () => {
    if (!showSubscriptionModal) return null;
    
    return (
      <div className={`subscription-modal ${showSubscriptionModal ? 'visible' : 'hidden'}`}>
        <div className="subscription-modal-content">
          <button 
            className="close-button"
            onClick={() => setShowSubscriptionModal(false)}
          >
            &times;
          </button>
          
          {!currentUser ? (
            <Auth initialTab={initialAuthTab} />
          ) : (
            <div className="subscription-info">
              <h2>Pro Calculator Access</h2>
              <p>During our beta period, all Pro features are available to logged-in users at no cost!</p>
              {currentUser && subscriptionDetails && (
                <p className="subscription-status">
                  Your current status: <span className="status-label beta-status">Beta Access</span>
                  {subscriptionDetails.type && subscriptionDetails.type !== 'beta' && (
                    <> (Special access type: {subscriptionDetails.type})</>  
                  )}
                </p>
              )}
              <div className="subscription-features">
                <h3>Pro Features Include:</h3>
                <ul>
                  <li>Save and load your cookie icing calculations</li>
                  <li>Advanced calculation options</li>
                  <li>Bulk recipe scaling</li>
                  <li>Priority customer support</li>
                </ul>
              </div>
              <div className="subscription-cta">
                <p className="beta-notice">Subscription options coming soon!</p>
                <p className="beta-notice">Enjoy free access to all Pro features during our beta period.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Render a preview banner for the Pro Calculator
  const renderPreviewBanner = () => {
    if (isPreview) {
      return (
        <div className="pro-preview-banner">
          <div className="pro-preview-content">
            <h3>Pro Calculator - Login Required</h3>
            <p>The Pro Calculator requires a free account to use. Sign up to access all features!</p>
            <div className="pro-preview-buttons">
              <button 
                className="pro-preview-button login"
                onClick={() => setShowSubscriptionModal(true)}
              >
                Login / Sign Up
              </button>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="pro-calculator-wrapper">
      {/* Preview banner */}
      {renderPreviewBanner()}
      
      {/* Auth error message */}
      {authError && (
        <div className="auth-error-message">
          {authError}
        </div>
      )}
      
      {/* Project manager modal */}
      {showProjectManager && (
        <div className="modal-overlay">
          <div className="modal-content project-manager-modal">
            <button className="modal-close" onClick={toggleProjectManager}>×</button>
            <h2>Project Management</h2>
            <p className="project-manager-info">Save your current cookie calculations or load a previously saved project.</p>
            <ProjectManager 
              calculatorState={savedState}
              onLoadProject={handleLoadProject}
              onProjectAction={handleProjectAction}
              isSubscribed={isSubscribed}
              showSaveForm={showSaveForm}
              setShowSaveForm={setShowSaveForm}
            />
          </div>
        </div>
      )}
      
      {/* Notification toast */}
      {notification.message && (
        <div className={`notification-toast ${notification.visible ? 'visible' : 'hidden'}`}>
          {notification.message}
        </div>
      )}
      
      {/* Main calculator component */}
      {!isPreview ? (
        currentUser ? (
          // Render the new ProCalculatorMain component
          <ProCalculatorMain 
            isDarkMode={isDarkMode}
            savedState={savedState}
            onStateChange={onStateChange}
            isSubscribed={isSubscribed}
            user={currentUser}
            subscriptionDetails={subscriptionDetails}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            isMetric={isMetric}
          />
        ) : (
          <ProCalculatorSignInPrompt />
        )
      ) : (
        <div className="pro-calculator-preview">
          <div className="pro-preview-message">
            <h2>Pro Calculator Features</h2>
            <p>The Pro Calculator requires a subscription after you sign up.</p>
            <ul className="pro-features-list">
              <li>Multiple cookie types in one calculation</li>
              <li>Save and load your calculations</li>
              <li>Set-based calculations for cookie collections</li>
              <li>Color percentage breakdowns</li>
              <li>Detailed results with color-by-color measurements</li>
            </ul>
            <button 
              className="pro-preview-cta"
              onClick={() => setShowSubscriptionModal(true)}
            >
              Sign Up to Access
            </button>
          </div>
        </div>
      )}
      
      {/* Subscription modal */}
      {renderSubscriptionModal()}
    </div>
  );
};

export default ProCalculatorWrapper;
